import {
  Box,
  BoxProps,
  Checkbox,
  Chip,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { WorkpackTypeFilterOption } from '@/__generated__/graphql';
import { useAtom } from 'jotai';
import * as kpiState from '../../state';
import { usePersistKpiFilters } from '../../hooks/usePersistKpiFilters';
import {
  kpiCheckboxStyles,
  kpiCloseIconStyles,
  kpiMenuItemStyles,
  kpiTypeChipStyles,
} from '../../styles';
import CancelIcon from '@mui/icons-material/Cancel';

const WORKPACK_TYPE_LABELS: Record<WorkpackTypeFilterOption, string> = {
  [WorkpackTypeFilterOption.Block]: 'Block',
  [WorkpackTypeFilterOption.BreakIn]: 'Break In',
  [WorkpackTypeFilterOption.Chase]: 'Chase',
  [WorkpackTypeFilterOption.Na]: 'N/A',
};

const ALL_WORKPACK_TYPES: WorkpackTypeFilterOption[] = Object.values(WorkpackTypeFilterOption);

type Props = BoxProps;

export const KpiWorkpackTypeFilterButton = ({ ...rest }: Props) => {
  const [selectedWorkpackTypes, setSelectedWorkpackTypes] = useAtom(kpiState.selectedWorkpackTypes);
  const { persistSelectedWorkpackTypesFilter, loading } = usePersistKpiFilters();

  const handleChange = (event: SelectChangeEvent<WorkpackTypeFilterOption[]>) => {
    const value = event.target.value as WorkpackTypeFilterOption[];
    setSelectedWorkpackTypes(value);
    persistSelectedWorkpackTypesFilter(value).catch();
  };

  const handleRemoveType = (typeToRemove: WorkpackTypeFilterOption) => {
    const newValue = selectedWorkpackTypes.filter((t) => t !== typeToRemove);
    setSelectedWorkpackTypes(newValue);
    persistSelectedWorkpackTypesFilter(newValue).catch();
  };

  const handleClearAll = () => {
    setSelectedWorkpackTypes([]);
    persistSelectedWorkpackTypesFilter([]).catch();
  };

  const renderSelectedValue = () => {
    if (selectedWorkpackTypes.length === 0) {
      return <Typography>Workpack types</Typography>;
    }

    const firstType = selectedWorkpackTypes[0];
    const remainingCount = selectedWorkpackTypes.length - 1;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <Chip
          size="small"
          label={WORKPACK_TYPE_LABELS[firstType]}
          onDelete={(e) => {
            e.stopPropagation();
            handleRemoveType(firstType);
          }}
          deleteIcon={<CancelIcon onMouseDown={(e) => e.stopPropagation()} />}
          sx={kpiTypeChipStyles}
        />
        {remainingCount > 0 && (
          <Typography variant="body2" color="primary.500" fontWeight={500}>
            +{remainingCount}
          </Typography>
        )}
        <CloseIcon
          onClick={(e) => {
            e.stopPropagation();
            handleClearAll();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          sx={kpiCloseIconStyles}
        />
      </Box>
    );
  };

  return (
    <Box {...rest}>
      <Select
        multiple
        variant="outlined"
        value={selectedWorkpackTypes}
        onChange={handleChange}
        id="kpi-tracker-workpack-type-filter"
        sx={{ maxHeight: '3.5rem' }}
        renderValue={renderSelectedValue}
        displayEmpty
        MenuProps={{
          slotProps: {
            paper: {
              sx: {
                borderRadius: '0.25rem',
                minWidth: '20rem',
              },
            },
          },
        }}
      >
        {ALL_WORKPACK_TYPES.map((type) => {
          const isSelected = selectedWorkpackTypes.includes(type);
          return (
            <MenuItem key={type} value={type} sx={kpiMenuItemStyles}>
              <Checkbox disabled={loading} checked={isSelected} sx={kpiCheckboxStyles} />
              <ListItemText
                primary={WORKPACK_TYPE_LABELS[type]}
                primaryTypographyProps={{
                  variant: 'body1',
                  color: 'primary.600',
                  fontWeight: 400,
                }}
              />
            </MenuItem>
          );
        })}
      </Select>
    </Box>
  );
};
